import { useRef, useState, useEffect } from 'react';
import type { ChangeEventHandler, DragEventHandler } from 'react';

import { UploadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';

import styles from './style.module.less';

type UploadCardProps = {
  title: string;
  description?: string;
  accept?: string;
  onUpload: (file: File) => void;
  buttonText?: string;
  initialFile?: File | null;
};

export const UploadCard = ({
  title,
  description,
  accept = '.xlsx,.xls,.csv',
  onUpload,
  buttonText = 'Selecionar Excel',
  initialFile = null,
}: UploadCardProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(initialFile || null);

  // Sync internal state with prop when it changes
  useEffect(() => {
    setUploadedFile(initialFile || null);
  }, [initialFile]);

  const handleSelect = () => inputRef.current?.click();

  const handleFile = (file: File) => {
    // Validate file type by extension (primary method)
    const validExtensions = accept.split(',').map((ext) => ext.trim().toLowerCase().replace(/^\./, ''));
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.includes('.') ? fileName.split('.').pop() || '' : '';

    // Check if file extension is valid
    const hasValidExtension = fileExtension && validExtensions.includes(fileExtension);

    // If extension is not valid, check MIME type as fallback
    if (!hasValidExtension) {
      const fileMimeType = file.type.toLowerCase();

      // Valid MIME types for Excel and Word files
      const validMimeTypes = [
        // Excel files
        'application/vnd.ms-excel', // .xls
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'text/csv', // .csv
        'application/csv', // .csv alternative
        'text/x-csv', // .csv alternative
        'application/excel', // .xls alternative
        'application/x-excel', // .xls alternative
        'application/x-msexcel', // .xls alternative
        // Word files
        'application/msword', // .doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/vnd.ms-word', // .doc alternative
        'application/x-msword', // .doc alternative
      ];

      // Check if MIME type is valid
      const hasValidMimeType = fileMimeType && validMimeTypes.some(mime =>
        fileMimeType.includes(mime) || mime.includes(fileMimeType)
      );

      // If neither extension nor MIME type is valid, reject
      if (!hasValidMimeType) {
        message.error(`Por favor, selecione um arquivo válido (${accept})`);
        return;
      }
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      message.error('O arquivo é muito grande. Tamanho máximo: 10MB');
      return;
    }

    setUploadedFile(file);
    onUpload(file);
    message.success(`Arquivo "${file.name}" carregado com sucesso!`);
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
      // reset so selecting the same file triggers change again
      e.currentTarget.value = '';
    }
  };

  const handleDragOver: DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave: DragEventHandler<HTMLDivElement> = () => {
    setIsDragging(false);
  };

  const handleDrop: DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  return (
    <div className={styles.container}>
      <div
        className={`${styles.dashedBox} ${isDragging ? styles.dragging : ''} ${uploadedFile ? styles.uploaded : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        onClick={handleSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelect();
          }
        }}
        aria-label={uploadedFile ? 'Trocar arquivo' : 'Selecione ou solte um arquivo aqui'}
      >
        {uploadedFile ? (
          <div className={styles.successIcon}>
            <CheckCircleOutlined />
          </div>
        ) : (
          <div className={styles.iconCircle} aria-hidden>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 16V8m0 0l-4 4m4-4l4 4" stroke="#0f9c9c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        )}

        <h3 className={styles.title}>{title}</h3>
        {description && <p className={styles.description}>{description}</p>}

        {uploadedFile && (
          <div className={styles.fileInfo}>
            <span className={styles.fileName}>{uploadedFile.name}</span>
            <span className={styles.fileSize}>
              ({(uploadedFile.size / 1024).toFixed(2)} KB)
            </span>
          </div>
        )}

        <input ref={inputRef} type="file" accept={accept} className={styles.hiddenInput} onChange={handleChange} />

        <Button
          type="primary"
          icon={uploadedFile ? <CheckCircleOutlined /> : <UploadOutlined />}
          onClick={handleSelect}
          className={styles.primaryBtn}
        >
          {uploadedFile ? 'Trocar arquivo' : buttonText}
        </Button>
      </div>
    </div>
  );
};
